import { AlgorithmTraceCallback, Particle, GenerationProgressCallback } from '../types/algorithm';
import { calculateParticleFitness } from '../utils/fitnessCalculator';
import { SeededRNG, createSeededRNG } from '../utils/seededRNG';

/**
 * Particle Swarm Optimization (PSO) implementation from scratch
 * Based on swarm intelligence with velocity and position updates
 */

/**
 * Initializes particle swarm
 */
export function initializeSwarm(
  swarmSize: number,
  taskCount: number,
  serviceCount: number,
  rng: SeededRNG
): Particle[] {
  const swarm: Particle[] = [];

  for (let i = 0; i < swarmSize; i++) {
    const particle: Particle = {
      position: new Array(taskCount),
      velocity: new Array(taskCount).fill(0),
      personalBest: new Array(taskCount),
      fitness: 0,
      personalBestFitness: 0,
      rwp: 0,
    };

    // Random initialization using seeded RNG
    for (let j = 0; j < taskCount; j++) {
      particle.position[j] = rng.randomInt(serviceCount);
    }

    particle.personalBest = [...particle.position];
    swarm.push(particle);
  }

  return swarm;
}

/**
 * Updates particle velocity and position using a standard discrete-PSO style
 * sigmoid transfer. The velocity update remains continuous, but movement in the
 * categorical service space is determined probabilistically.
 */
export function updateParticle(
  particle: Particle,
  globalBest: Particle,
  taskCount: number,
  serviceCount: number,
  w: number, // inertia weight
  c1: number, // cognitive parameter
  c2: number, // social parameter
  rng: SeededRNG
): void {
  for (let i = 0; i < taskCount; i++) {
    const r1 = rng.random();
    const r2 = rng.random();

    // Update velocity (conceptual, used to determine movement probability)
    const cognitive = c1 * r1 * (particle.personalBest[i] - particle.position[i]);
    const social = c2 * r2 * (globalBest.position[i] - particle.position[i]);
    const velocityComponent = w * particle.velocity[i] + cognitive + social;

    // Clamp velocity
    const maxVelocity = 5.0;
    particle.velocity[i] = Math.max(-maxVelocity, Math.min(maxVelocity, velocityComponent));

    const transferProbability = 1 / (1 + Math.exp(-particle.velocity[i]));
    const guideWeight = c1 * r1 + c2 * r2;
    const followPersonalProbability = guideWeight === 0 ? 0.5 : (c1 * r1) / guideWeight;
    const guidePosition = rng.random() < followPersonalProbability
      ? particle.personalBest[i]
      : globalBest.position[i];

    if (rng.random() < transferProbability) {
      particle.position[i] = guidePosition;
    } else if (rng.randomChance(0.1)) {
      particle.position[i] = rng.randomInt(serviceCount);
    }
  }
}

export function cloneParticle(particle: Particle): Particle {
  return {
    position: [...particle.position],
    velocity: [...particle.velocity],
    personalBest: [...particle.personalBest],
    fitness: particle.fitness,
    personalBestFitness: particle.personalBestFitness,
    rwp: particle.rwp,
  };
}

function cloneParticlePersonalBest(particle: Particle): Particle {
  return {
    position: [...particle.personalBest],
    velocity: [...particle.velocity],
    personalBest: [...particle.personalBest],
    fitness: particle.personalBestFitness,
    personalBestFitness: particle.personalBestFitness,
    rwp: particle.rwp,
  };
}

/**
 * Finds the historical global-best particle snapshot.
 *
 * PSO convergence should be based on best-so-far, not only the current swarm
 * state. Returning a clone avoids mutating the global guide when the source
 * particle moves in a later update.
 */
export function findGlobalBest(swarm: Particle[]): Particle {
  let best = swarm[0];
  for (const particle of swarm) {
    if (particle.personalBestFitness > best.personalBestFitness) {
      best = particle;
    }
  }
  return cloneParticlePersonalBest(best);
}

/**
 * PSO evolution for one generation
 */
function evolvePSO(
  swarm: Particle[],
  globalBest: Particle,
  taskCount: number,
  serviceCount: number,
  matrix: number[][][],
  generation: number,
  maxGenerations: number,
  rng: SeededRNG,
  trace?: AlgorithmTraceCallback
): void {
  // Linearly decreasing inertia weight: 0.9 -> 0.4
  const wMax = 0.9;
  const wMin = 0.4;
  const w = wMax - ((wMax - wMin) * generation) / maxGenerations;

  const c1 = 2.0; // cognitive parameter
  const c2 = 2.0; // social parameter

  for (let particleIndex = 0; particleIndex < swarm.length; particleIndex++) {
    const particle = swarm[particleIndex];
    const previousFitness = particle.fitness;
    const previousPosition = [...particle.position];
    // Update velocity and position
    updateParticle(particle, globalBest, taskCount, serviceCount, w, c1, c2, rng);
    const changedGenes = particle.position.reduce(
      (count, value, index) => count + (value !== previousPosition[index] ? 1 : 0),
      0
    );
    trace?.({
      generation: generation + 1,
      phase: 'movement',
      action: 'update_velocity_position',
      detail: 'Updated particle velocity and discrete service position.',
      entityIndex: particleIndex,
      metadata: { changedGenes, inertiaWeight: w },
    });

    // Evaluate fitness and update personal best
    calculateParticleFitness(particle, matrix, taskCount, serviceCount);
    trace?.({
      generation: generation + 1,
      phase: 'evaluation',
      action: 'evaluate_particle',
      detail: 'Evaluated particle fitness after movement.',
      entityIndex: particleIndex,
      previousFitness,
      fitness: particle.fitness,
    });
    
    // Update personal best if current fitness is better
    if (particle.fitness > particle.personalBestFitness) {
      particle.personalBestFitness = particle.fitness;
      particle.personalBest = [...particle.position];
      trace?.({
        generation: generation + 1,
        phase: 'best_update',
        action: 'update_personal_best',
        detail: 'Updated particle personal best.',
        entityIndex: particleIndex,
        fitness: particle.fitness,
        accepted: true,
        solution: particle.position,
      });
    }
  }
}

/**
 * Runs PSO for specified generations
 */
export async function runPSO(
  taskCount: number,
  serviceCount: number,
  matrix: number[][][],
  parameters: {
    populationSize: number;
    generations: number;
  },
  onGenerationComplete?: GenerationProgressCallback,
  seed?: number,
  runtimeOptions?: {
    yieldForProgress?: boolean;
    reportEveryGeneration?: boolean;
    trace?: AlgorithmTraceCallback;
  }
): Promise<Particle> {
  // Create seeded RNG for reproducibility
  const rng = createSeededRNG(seed);
  const shouldYieldForProgress = runtimeOptions?.yieldForProgress ?? true;
  const shouldReportEveryGeneration = runtimeOptions?.reportEveryGeneration ?? false;
  const trace = runtimeOptions?.trace;
  
  // Initialize swarm
  let swarm = initializeSwarm(parameters.populationSize, taskCount, serviceCount, rng);
  trace?.({
    generation: 0,
    phase: 'initialization',
    action: 'initialize_swarm',
    detail: `Created ${parameters.populationSize} particles.`,
    metadata: { populationSize: parameters.populationSize, taskCount, serviceCount },
  });

  // Evaluate initial swarm
  for (const particle of swarm) {
    calculateParticleFitness(particle, matrix, taskCount, serviceCount);
    // Initialize personal best
    particle.personalBestFitness = particle.fitness;
    particle.personalBest = [...particle.position];
  }

  let globalBest = findGlobalBest(swarm);
  trace?.({
    generation: 0,
    phase: 'evaluation',
    action: 'evaluate_initial_swarm',
    detail: 'Evaluated all particles and initialized personal/global bests.',
    bestFitness: globalBest.fitness,
    solution: globalBest.position,
  });

  // Evolution loop
  let lastUpdateTime = Date.now();
  const UPDATE_INTERVAL = 100; // Update UI every 100ms (PERFORMANCE OPTIMIZATION)
  
  for (let gen = 0; gen < parameters.generations; gen++) {
    evolvePSO(swarm, globalBest, taskCount, serviceCount, matrix, gen, parameters.generations, rng, trace);
    const candidateGlobalBest = findGlobalBest(swarm);
    if (candidateGlobalBest.fitness > globalBest.fitness) {
      globalBest = candidateGlobalBest;
      trace?.({
        generation: gen + 1,
        phase: 'best_update',
        action: 'update_global_best',
        detail: 'Updated global best particle.',
        bestFitness: globalBest.fitness,
        accepted: true,
        solution: globalBest.position,
      });
    }

    // Batch UI updates - only update every 100ms or every 10 generations (PERFORMANCE OPTIMIZATION)
    const now = Date.now();
    const shouldUpdate =
      shouldReportEveryGeneration ||
      (now - lastUpdateTime > UPDATE_INTERVAL) ||
      (gen % 10 === 9) ||
      (gen === parameters.generations - 1);
    
    if (shouldUpdate) {
      if (onGenerationComplete) {
        onGenerationComplete(gen + 1, globalBest.fitness, globalBest.position);
      }
      trace?.({
        generation: gen + 1,
        phase: 'generation',
        action: 'generation_complete',
        detail: 'Generation best particle recorded.',
        bestFitness: globalBest.fitness,
        solution: globalBest.position,
      });
      lastUpdateTime = now;
      if (shouldYieldForProgress) {
        // Yield control to allow UI updates when running interactively.
        await new Promise(resolve => setTimeout(resolve, 0));
      }
    }
  }

  return globalBest;
}
